import React, { createRef, useRef, useState } from 'react'
import {
  ArrowIcon,
  Container,
  Dot,
  DotContainer,
  InnerContainer
} from './DotNavigation.styles'
import { DotNavigationProps } from './DotNavigation.type'
import useKeyboardNavigation from './useKeyboardNavigation'
import { useIsMobile } from '~/hooks/useIsMobile'

const DESKTOP_RANGE = 2
const MOBILE_RANGE = 1
const DESKTOP_DOT_SIZE = 72
const MOBILE_DOT_SIZE = 55

const DotNavigation = ({
  length = 3,
  selected,
  onSelect,
  disabled,
  nonFocusable,
  color
}: DotNavigationProps) => {
  const [focusedDot, setFocusedDot] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const isMobile = useIsMobile()

  const baseDotSize = isMobile ? MOBILE_DOT_SIZE : DESKTOP_DOT_SIZE
  const baseDisplayRange = isMobile ? MOBILE_RANGE : DESKTOP_RANGE

  const dotRefs = useRef<React.RefObject<HTMLDivElement>[]>(
    Array.from({ length }, () => createRef<HTMLDivElement>())
  )

  const dots = Array.from({ length }, (value, index) => index)

  const {
    leftArrowNavigation,
    rightArrowNavigation,
    handleKeyPress,
    handleLeftArrowKeyPress,
    handleRightArrowKeyPress
  } = useKeyboardNavigation({
    length,
    focusedDot,
    selected,
    onSelect,
    dotRefs
  })

  const onFocus = (dot: number) => {
    setFocusedDot(dot)

    if (!containerRef.current) {
      return
    }

    containerRef.current?.scroll(
      baseDotSize * dot - baseDotSize * baseDisplayRange,
      0
    )
  }

  const getDotSize = (index: number) => {
    const differenceBetweenPositions = Math.abs(focusedDot - index)

    if (differenceBetweenPositions <= baseDisplayRange - 1) {
      return 'large'
    }

    if (differenceBetweenPositions === baseDisplayRange) {
      return 'regular'
    }

    return 'small'
  }

  return (
    <Container disabled={disabled}>
      <ArrowIcon
        iconName='DropdownArrowLeft'
        onClick={leftArrowNavigation}
        onKeyDown={handleLeftArrowKeyPress}
        disabled={disabled}
        role='button'
        nonFocusable={nonFocusable}
      />
      <InnerContainer ref={containerRef}>
        {dots.map((dot) => (
          <DotContainer
            key={dot}
            nonFocusable={nonFocusable}
            isSelected={dot === selected}
            color={color}
          >
            <Dot
              ref={dotRefs.current?.[dot]}
              isSelected={dot === selected}
              onMouseDown={() => onSelect(dot)}
              onKeyDown={(event) => handleKeyPress(event, dot)}
              onFocus={() => onFocus(dot)}
              onBlur={() => onFocus(selected)}
              size={getDotSize(dot)}
              role='button'
              disabled={disabled}
              tabIndex={disabled || nonFocusable ? -1 : 0}
              color={color}
            />
          </DotContainer>
        ))}
      </InnerContainer>
      <ArrowIcon
        iconName='DropdownArrowRight'
        onClick={rightArrowNavigation}
        onKeyDown={handleRightArrowKeyPress}
        disabled={disabled}
        role='button'
        nonFocusable={nonFocusable}
      />
    </Container>
  )
}

export default DotNavigation
